import React, { useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react';
import { VarContext } from '../Context/VarContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSignOutAlt, faCheck, faStar } from '@fortawesome/free-solid-svg-icons'
import { apiGet, apiPost } from '../utils/api'; // ✅ Importar utilidades API

import './Dashboard.css';
//import Nav from '../Components/Nav'

// Imágenes de usuario/dashboard
import imgFemsaOscuro from '../user/logo_femsa_oscuro.svg';
import imgMircVerde from '../user/log_mirc_verde.svg';
import imgIlustracion2 from '../user/imagen2.png';
import iconBien from '../user/bien.svg';
import iconEstrella from '../user/estrella.svg';
import iconReloj from '../user/reloj.svg';

const Dashboard = () => {
	const GConText = useContext(VarContext);
	const userId = GConText.userId;
	const setStatus = GConText.setStatus;

	// Nuevo: estado para mostrar cursos asignados y previsualizarlos en iframe
	const [assignedScorms, setAssignedScorms] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [previewScormId, setPreviewScormId] = useState(null);
	const [scormSummaries, setScormSummaries] = useState({}); // Summaries calculados (score, status, etc)
	const [overallProgress, setOverallProgress] = useState(null);
	const [showLogoutModal, setShowLogoutModal] = useState(false);
	const iframeRef = useRef(null);

	const closePreview = () => {
		setPreviewScormId(null);
		try {
			if (iframeRef.current) iframeRef.current.src = 'about:blank';
		} catch (e) {}
	};

	// Si cambia el usuario en una SPA (sin recargar la página), forzar recarga del SCORM
	// para evitar que el iframe muestre estado visual del usuario anterior.
	useEffect(() => {
		try {
			if (iframeRef.current) iframeRef.current.src = 'about:blank';
		} catch (e) {}
	}, [userId]);


	// Actualizar progreso solo cuando el SCORM reporta cambios (sin polling)
	useEffect(() => {
		if (!previewScormId) return;
		
		// Verificación inicial al abrir el curso
		const checkProgress = async () => {
			try {
				const progressRes = await apiGet('/scorm/progress/me');
				if (progressRes.ok) {
					const progressData = await progressRes.json();

					// 🔍 LOG LLAMATIVO: Mostrar datos del curso actual
					/* const currentSummary = progressData.summaries?.[previewScormId];
					if (currentSummary) {
						console.log(
							'%c📊 PROGRESO SCORM DESDE DYNAMODB',
							'background: #4CAF50; color: white; font-size: 14px; font-weight: bold; padding: 2px 12px; border-radius: 4px;',
							{
								curso: previewScormId,
								status: currentSummary.status,
								score: currentSummary.score,
								bestScore: currentSummary.bestScore,
								lastUpdated: currentSummary.lastUpdated,
								completado: ['completed', 'passed', 'complete', 'success', 'true', '1'].includes(
									String(currentSummary.status || '').toLowerCase().trim()
								)
							}
						);
					} */
					
					// 🔍 LOG LLAMATIVO: Progreso global
					/* if (progressData.overall) {
						console.log(
							'%c🎯 PROGRESO GLOBAL',
							'background: #2196F3; color: white; font-size: 14px; font-weight: bold; padding: 2px 12px; border-radius: 4px;',
							{
								completados: progressData.overall.completedCount,
								asignados: progressData.overall.assignedCount,
								porciento: progressData.overall.percent + '%',
								todoCompletado: progressData.overall.isComplete
							}
						);
					} */

					setScormSummaries(progressData.summaries || {});
					setOverallProgress(progressData.overall || null);
					try {
						if (progressData?.overall?.isComplete != null) {
							setStatus(!!progressData.overall.isComplete);
						}
					} catch (e) {}
				}
			} catch (e) {
				console.error('❌ Error obteniendo progreso:', e);
			}
		};
		
		checkProgress();
		
		return () => {
			setTimeout(checkProgress, 1500);
		};
	}, [previewScormId, setStatus]);

	useEffect(() => {
		const API_BASE = process.env.REACT_APP_API_BASE || '';
		const load = async () => {
			try {
				// ✅ Usar apiGet en lugar de fetch manual
				const res = await apiGet('/scorm/my');
				if (!res.ok) {
					setAssignedScorms([]);
					return;
				}
				const data = await res.json();
				const assignedScormIds = (data.scorms || []).map(s => s.id);
				setAssignedScorms(data.scorms || []);

				// Progreso viene incluido en /scorm/my
				if (data.summaries) setScormSummaries(data.summaries);
				if (data.overall) {
					setOverallProgress(data.overall);
					try { if (typeof data.overall.isComplete === 'boolean') setStatus(!!data.overall.isComplete); } catch (e) {}
				}
				
				// 🧹 Limpiar localStorage de cursos que ya NO están asignados (solo del usuario actual)
				try {
					// Obtener userId desde el token (JWT) para no mezclar usuarios
					let currentUserId = null;
					const token = sessionStorage.getItem('accessToken');
					try {
						if (token) {
							const parts = String(token).split('.');
							if (parts.length >= 2) {
								const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
								const json = decodeURIComponent(atob(payloadB64).split('').map(c =>
									'%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
								).join(''));
								const payload = JSON.parse(json);
								currentUserId = payload && payload.id ? String(payload.id) : null;
							}
						}
					} catch (e) { currentUserId = null; }

					const allKeys = Object.keys(localStorage);
					const scormKeys = allKeys.filter(key => key.startsWith('scorm_'));
					
					for (const key of scormKeys) {
						// Formato nuevo: scorm_<userId>_<scormId>_<campo>
						const matchNew = key.match(/^scorm_([^_]+)_(scorm-[^_]+)_/);
						if (matchNew && matchNew[1] && matchNew[2]) {
							const keyUserId = matchNew[1];
							const keyScormId = matchNew[2];
							if (currentUserId && keyUserId !== currentUserId) {
								// No tocar progreso de otros usuarios en este navegador
								continue;
							}
							if (!assignedScormIds.includes(keyScormId)) {
								localStorage.removeItem(key);
							}
							continue;
						}

						// Formato legacy: scorm_<scormId>_<campo>
						const matchLegacy = key.match(/^scorm_(scorm-[^_]+)_/);
						if (matchLegacy && matchLegacy[1]) {
							const keyScormId = matchLegacy[1];
							if (!assignedScormIds.includes(keyScormId)) {
								localStorage.removeItem(key);
							}
						}
					}
				} catch (e) {
					console.error('Error limpiando localStorage:', e);
				}
				
				// Progreso ya viene incluido en /scorm/my (arriba)
				
				//POR DEFUALT SE CARGA EL PRIMERO SI ESTO ESTA ACTIVO
				//if ((data.scorms || []).length > 0) setSelectedScorm(data.scorms[0].id);
			} catch (e) {
				console.error('Error loading my scorms', e);
			}
			setIsLoading(false);
		};
		load();
		
		// Debounce timer para agrupar múltiples guardados del SCORM
		let debounceTimer = null;
		
		// Función para refrescar progreso
		const refreshProgress = async () => {
			try {
				const progressRes = await apiGet('/scorm/progress/me');
				if (progressRes.ok) {
					const progressData = await progressRes.json();
					setScormSummaries(progressData.summaries || {});
					setOverallProgress(progressData.overall || null);
					try {
						if (progressData && progressData.overall && typeof progressData.overall.isComplete === 'boolean') {
							setStatus(!!progressData.overall.isComplete);
						}
					} catch (e) {}
				}
			} catch (e) {
				console.error('Error refreshing progress', e);
			}
		};
		
		// Listener para actualizar progreso SOLO cuando el SCORM guarda datos
		const handleScormSaved = async (event) => {
			try {
				if (event.data?.type === 'scorm_saved') {
					refreshProgress();
				}
			} catch (e) {}
		};
		
		window.addEventListener('message', handleScormSaved);
		
		return () => {
			window.removeEventListener('message', handleScormSaved);
			if (debounceTimer) clearTimeout(debounceTimer);
		};
	}, [userId, setStatus]);

	// ===== Handshake de token con el iframe del curso =====
	// El token de sesión ya no viaja en la URL del lanzador (CWE-598: quedaría
	// en el historial del navegador, en el header Referer y en los access logs).
	// El wrapper SCORM avisa que está listo con un mensaje 'scorm_ready' y le
	// respondemos con el token, dirigido sólo al origen donde vive la API.
	const scormOrigin = useMemo(() => {
		const base = process.env.REACT_APP_API_BASE || '';
		try {
			return base ? new URL(base, window.location.href).origin : window.location.origin;
		} catch (e) {
			return window.location.origin;
		}
	}, []);

	const sendTokenToScorm = useCallback(() => {
		try {
			const token = sessionStorage.getItem('accessToken');
			const frame = iframeRef.current;
			if (!token || !frame || !frame.contentWindow) return;
			frame.contentWindow.postMessage({ type: 'scorm_token', token }, scormOrigin);
		} catch (e) { /* ignore */ }
	}, [scormOrigin]);

	useEffect(() => {
		const handleScormReady = (event) => {
			if (event.origin !== scormOrigin) return;
			if (event.data?.type !== 'scorm_ready') return;
			// Sólo se responde al iframe del curso, no a cualquier ventana.
			if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
			sendTokenToScorm();
		};
		window.addEventListener('message', handleScormReady);
		return () => window.removeEventListener('message', handleScormReady);
	}, [scormOrigin, sendTokenToScorm]);

	// Evitar scrollbar horizontal exterior mientras estamos en el dashboard
	useEffect(() => {
		const prev = document.documentElement.style.overflowX;
		document.documentElement.style.overflowX = 'hidden';
		return () => { document.documentElement.style.overflowX = prev || ''; };
	}, []);

	// Reemplazo simple del dashboard: menú de cursos asignados + iframe
	const replaceDashboardWithScormMenu = true; // cambiar a false si quieres conservar el dashboard original

// Función para logout
const handleLogout = async () => {
	try {
		// ✅ Usar apiPost en lugar de fetch manual
		await apiPost('/logout');

		// Limpiar datos locales
		sessionStorage.removeItem('accessToken');
		localStorage.removeItem('refreshToken');

		// Limpiar contexto
		GConText.setUserId('');
		GConText.setNombre('');
		GConText.setType('');
		GConText.setBookmark('');
		GConText.setUsername('');
		GConText.setPassword('');

		window.location.href = '/';

	} catch (error) {
		console.error('Error en logout:', error);
		sessionStorage.removeItem('accessToken');
		localStorage.removeItem('refreshToken');
		GConText.setUserId('');
		GConText.setNombre('');
		GConText.setType('');
		GConText.setBookmark('');
		GConText.setUsername('');
		GConText.setPassword('');
		window.location.href = 'https://intelego.com.mx/lms';
	}
};
	if (replaceDashboardWithScormMenu) {
		const API_BASE = process.env.REACT_APP_API_BASE || '';
		const displayName = (GConText.nombre || GConText.username || '').trim();
		const clampPercent = (n) => {
			const num = Number(n);
			if (!Number.isFinite(num)) return 0;
			return Math.max(0, Math.min(100, Math.round(num)));
		};

		const getProgressColor = (percent) => {
			const p = clampPercent(percent);
			// Escala solicitada (misma que Admin):
			// 0%: #d2d1d1 | 1-24%: #ff6361 | 25-49%: #ff7f36 | 50-74%: #eda200 | 75-99%: #c4c400 | 100%: #80e30f
			if (p >= 100) return '#80e30f';
			if (p >= 75) return '#c4c400';
			if (p >= 50) return '#eda200';
			if (p >= 25) return '#ff7f36';
			if (p >= 1) return '#ff6361';
			return '#d2d1d1';
		};
		const computedOverall = (() => {
			// Preferir cálculo del backend si existe
			if (overallProgress && typeof overallProgress.percent === 'number') return overallProgress;
			const total = (assignedScorms || []).length;
			if (!total) return { assignedCount: 0, completedCount: 0, percent: 0, isComplete: false };
			let completed = 0;
			for (const s of (assignedScorms || [])) {
				const summary = scormSummaries[s.id] || {};
				const statusLower = String(summary.status || '').toLowerCase().trim();
				const isCompleted = ['completed', 'passed', 'complete', 'success', 'true', '1'].includes(statusLower);
				if (isCompleted) completed++;
			}
			const percent = Math.round((completed / total) * 100);
			return { assignedCount: total, completedCount: completed, percent, isComplete: completed === total };
		})();
		const previewCourseTitle = (() => {
			if (!previewScormId) return '';
			const found = (assignedScorms || []).find((x) => x && x.id === previewScormId);
			return (found && (found.title || found.name)) ? String(found.title || found.name) : '';
		})();
		if (isLoading) {
			return (
				<div style={{ minHeight: '100vh', background: '#FF585D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<div style={{ textAlign: 'center', color: '#fff' }}>
						<div className="spinner-border mb-3" role="status" style={{ width: 48, height: 48 }}></div>
						<div style={{ fontSize: 16 }}>Cargando cursos...</div>
					</div>
				</div>
			);
		}

		return (
			<div className="dash-page">
				{/* Navbar */}
				<nav className="dash-navbar">
					<div className="dash-navbar__left">
						<img src={imgFemsaOscuro} alt="FEMSA" className="dash-navbar__logo" />
						<img src={imgMircVerde} alt="MIRC" className="dash-navbar__logo-mirc" />
						<span className="dash-navbar__title">Manejo de Incidentes y Resolución de Crisis</span>
					</div>
					<button onClick={() => setShowLogoutModal(true)} className="dash-navbar__logout">
						Cerrar sesión
					</button>
				</nav>

				{/* Banner */}
				<div className="dash-banner">
					<img src={imgIlustracion2} alt="" className="dash-banner__img" />
					<div className="dash-banner__content">
						<div className="dash-banner__kicker">Mi Capacitación</div>
						<h2 className="dash-banner__greeting">Hola, {displayName || 'Usuario'}</h2>
						<p className="dash-banner__subtitle">Desarrolla tu potencial en cada etapa de aprendizaje.</p>
						<div className="dash-banner__progress">
							<div className="dash-banner__progress-bar">
								<div className="dash-banner__progress-fill" style={{ width: `${clampPercent(computedOverall.percent)}%` }} />
							</div>
							<span className="dash-banner__progress-text">{computedOverall.completedCount} de {computedOverall.assignedCount} cursos completados</span>
						</div>
					</div>
				</div>

				{/* Cursos */}
				<div className="dash-courses">
					<h3 className="dash-courses__title">Continúa aprendiendo</h3>

					{assignedScorms.length === 0 ? (
						<div style={{ color: '#999', fontSize: 14 }}>No tienes cursos asignados</div>
					) : (
						<div className="dash-courses__grid">
							{[...assignedScorms].sort((a, b) => ((a.title || a.id || '').localeCompare(b.title || b.id || ''))).map((s) => {
								const summary = scormSummaries[s.id] || {};
								const statusLower = String(summary.status || '').toLowerCase().trim();
								const isCompleted = ['completed', 'passed', 'complete', 'success', 'true', '1'].includes(statusLower);
								const inProgress = statusLower && !isCompleted;
								const score = summary.score != null ? Math.round(summary.score) : null;

								return (
									<div key={s.id} className="dash-course-card">
										<div>
											<h4 className="dash-course-card__title">{s.title || s.id}</h4>
											<div className="dash-course-card__meta">
												{isCompleted ? (
													<>
														<span className="dash-course-card__status dash-course-card__status--done"><img src={iconBien} alt="" style={{ width: 20, height: 20 }} /> Completado</span>
														{score != null && <span className="dash-course-card__status dash-course-card__status--score"><img src={iconEstrella} alt="" style={{ width: 20, height: 20 }} /> {score} pts.</span>}
													</>
												) : inProgress ? (
													<span className="dash-course-card__status"><img src={iconReloj} alt="" style={{ width: 20, height: 20 }} /> En progreso</span>
												) : (
													<span className="dash-course-card__status"><img src={iconReloj} alt="" style={{ width: 20, height: 20 }} /> Por iniciar</span>
												)}
											</div>
										</div>
										<button
											onClick={() => setPreviewScormId(s.id)}
											className="btn-login dash-course-card__btn"
										>
											Ver curso
										</button>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Modal SCORM */}
				{previewScormId && (() => {
					const currentSummary = scormSummaries[previewScormId] || {};
					const statusLower = String(currentSummary.status || '').toLowerCase().trim();
					const isCompleted = ['completed', 'passed', 'complete', 'success', 'true', '1'].includes(statusLower);
					const score = currentSummary.score != null ? Math.round(currentSummary.score) : null;

					return (
						<div role="dialog" aria-modal="true" className="scorm-modal-overlay">
							<div className="scorm-modal">
								<div className="scorm-modal__header">
									<div className="d-flex align-items-center gap-2">
										<strong>{previewCourseTitle || `Curso: ${previewScormId}`}</strong>
										{isCompleted && (
											<span className="btn_ch bg-verde c-blanco" style={{ fontSize: '12px', padding: '4px 8px' }}>
												<FontAwesomeIcon icon={faCheck} className="me-1" /> Completado
											</span>
										)}
										{score != null && (
											<span className="btn_ch bg-amarillo c-negro" style={{ fontSize: '12px', padding: '4px 8px' }}>
												<FontAwesomeIcon icon={faStar} className="me-1" /> {score}
											</span>
										)}
									</div>
									<div>
										<button className="btn-login" onClick={closePreview}>Cerrar curso</button>
									</div>
								</div>
								<iframe
									key={`${GConText.userId || 'anon'}-${previewScormId}`}
									title={`scorm-${previewScormId}`}
									src={`${API_BASE}/scorm/launch/${previewScormId}`}
									style={{ width: '100%', height: '100%', border: 'none' }}
									ref={iframeRef}
									onLoad={sendTokenToScorm}
								/>
							</div>
						</div>
					);
				})()}

				{/* Modal Logout */}
				{showLogoutModal && (
					<div style={{ position: 'fixed', inset: 0, backdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
						<div style={{ background: '#fff', borderRadius: 20, padding: '40px', maxWidth: 500, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
							<h3 style={{ fontSize: 28, fontWeight: 700, marginBottom: 12 }}>¿Quieres cerrar sesión?</h3>
							<p style={{ fontSize: 18, color: '#555', marginBottom: 24, lineHeight: 1.5 }}>Tu avance se guardará automáticamente.<br/>Podrás continuar cuando vuelvas a ingresar.</p>
							<div style={{ display: 'flex', gap: 12 }}>
								<button className='btn-cancel' onClick={() => setShowLogoutModal(false)}>Cancelar</button>
								<button className='btn-login' onClick={handleLogout}>Cerrar sesión</button>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}
}

export default Dashboard
